import { injectable, inject } from '@patsnap/tahiti-core/shared/inversify';
import { Emitter, type Event } from '@patsnap/tahiti-core/common/event';
import { TranslationSettingModel } from '../model/translation-settings.model';
import { DEFAULT_LANGUAGE } from '../constants';
import { LangTypeMode } from '../type/translation.enum';
import type { TranslationSettings, SupportedSide, TargetSetting, TargetVisibleSetting } from '../type/translation.type';

export const TranslationSettingsService = Symbol('TranslationSettingsService');

export interface TranslationSettingsService {
    readonly onStatesChange: Event<TranslationSettings>;

    getModelData(): TranslationSettings;
    setTranslation(target: TargetSetting): void;
    setTranslationVisible(target: TargetVisibleSetting): void;
}

@injectable()
export class TranslationSettingsServiceImpl implements TranslationSettingsService {
    private _onStatesChangeEmitter = new Emitter<TranslationSettings>();
    readonly onStatesChange = this._onStatesChangeEmitter.event;

    constructor(
        @inject(TranslationSettingModel)
        private _model: TranslationSettingModel,
    ) {
        this._model.onStatesChange(this._emitModelDataChange.bind(this));
    }

    private _emitModelDataChange() {
        this._onStatesChangeEmitter.fire(this.getModelData());
    }

    public getModelData() {
        return this._model.modelData;
    }

    private _setModelData(value: Partial<TranslationSettings>) {
        this._model.update(modelData => {
            return { ...modelData, ...value };
        });
    }

    private _getTargetValue(lang: string, langType: LangTypeMode) {
        const isEnabled = langType !== LangTypeMode.DEFAULT;

        return {
            lang,
            langType,
            isActivated: isEnabled,
            isVisible: isEnabled,
        };
    }

    private _setTranslationBySide(lang: string, langType: LangTypeMode, side: SupportedSide) {
        const targetValue = this._getTargetValue(lang, langType);

        this._setModelData({
            [side]: targetValue,
        });
    }

    private _setTranslationBoth(lang: string, langType: LangTypeMode) {
        const targetValue = this._getTargetValue(lang, langType);

        this._setModelData({
            left: { ...targetValue },
            right: { ...targetValue },
        });
    }

    public setTranslation({ side = 'left', lang = DEFAULT_LANGUAGE, langType = LangTypeMode.DEFAULT }: TargetSetting) {
        if (side === 'both') {
            this._setTranslationBoth(lang, langType);
        } else {
            this._setTranslationBySide(lang, langType, side);
        }
    }

    private _setTranslationVisibleBySide(isVisible: boolean, side: SupportedSide) {
        const modelData = this.getModelData();

        modelData[side].isVisible = isVisible;

        this._setModelData(modelData);
    }

    private _setTranslationVisibleBoth(isVisible: boolean) {
        const modelData = this.getModelData();

        modelData.left.isVisible = isVisible;
        modelData.right.isVisible = isVisible;

        this._setModelData(modelData);
    }

    public setTranslationVisible({ side = 'both', isVisible }: TargetVisibleSetting) {
        if (side === 'both') {
            this._setTranslationVisibleBoth(isVisible);
        } else {
            this._setTranslationVisibleBySide(isVisible, side);
        }
    }
}
